/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * Module-level bus between the PDP hero rail ask and the Owners section: the ask lives inside
 * `ProductHero` (the rail), the band and the author's own note live in the section far below.
 * Module-level so neither component needs a shared ancestor prop.
 *
 *  - rail → section: "open the band" (`requestOpenOwnerBand` / `onOpenOwnerBand`).
 *    (A guide's pick blocks asked the capture block for its chooser the same way, 2026-10-07 →
 *    2026-10-17; the banner is the one declare ask now, so that channel is gone.)
 *  - section → rail: the reader's own-note state (`publishOwnNote` / `useOwnNoteSignal`), so the
 *    rail says "See your review" to an author instead of asking again (decided 2026-09-13).
 */
import { useEffect, useState } from 'react'

/**
 * Where an "open the band" request came from — the `location` the band reports on
 * `band_opened`, so an ask's opens sit under its own impressions in GA4 (2026-09-18: a rail
 * click scrolled to the section and the band reported `section`, so the rail's opens were the
 * section's and the rail's own rate read as zero). `own_note` = an edit from the reader's card:
 * not an ask converting, so it leaves the funnel's numerator.
 */
export type OwnerBandOrigin = 'rail' | 'section' | 'strip' | 'seated' | 'roster' | 'own_note'

/** `buyAgain`: the ask was the form's own buy-again question and the reader answered it (the
 *  guide's own roster row, 2026-10-07) — the band opens seeded, as the strip's Yes / No does. */
type OpenBandListener = (origin?: OwnerBandOrigin, buyAgain?: boolean) => void

const listeners = new Set<OpenBandListener>()

export function requestOpenOwnerBand(origin?: OwnerBandOrigin, buyAgain?: boolean): void {
  listeners.forEach(fn => fn(origin, buyAgain))
}

export function onOpenOwnerBand(fn: OpenBandListener): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

// ── The seated surface ────────────────────────────────────────────────────────
// A guide article can hold the reader's own note in two places: the capture strip (which owns
// the declaration) and the block of the pick they own — its roster row, or its in-depth block
// when the pick has no row (2026-10-17: one seat per page). Two `OwnNoteCard`s for one note
// means two keep-it-yours sign-ins and two `useKeepSurface` registrations (Directive 3b903d15),
// so the seated card wins where it exists and says so here; the strip falls back to its status
// chip. The seat also lends the band its slot, so the form opens under the ask that opened it.

let seatedSurface = false
/** The seat's band slot: where `GuideCapture` portals the page's one band (2026-10-17). */
let seatedSlot: HTMLElement | null = null
/** Who holds the seat: a token the seating block made for itself. One seat per page, so a
 *  second block that matches the declared product (an alias group, a product rostered twice)
 *  finds it taken and stands down; and only the holder's `false` can empty it (7d70977c). */
let seatedOwner: string | null = null
const seatedListeners = new Set<(shown: boolean) => void>()
const slotListeners = new Set<(slot: HTMLElement | null) => void>()
const ownerListeners = new Set<(owner: string | null) => void>()

/** The pick's block is showing the reader's own note or the seated ask. `slot`, when the block
 *  has one, is the element the band renders into — in place, under the ask, instead of at the
 *  capture block at the top of the page (2026-10-17: a far ask scrolled the page to the band).
 *  `owner` names the block; a block that does not hold the seat changes nothing. */
export function publishSeatedSurface(
  shown: boolean,
  slot: HTMLElement | null = null,
  owner = 'seat'
): void {
  if (shown) {
    if (seatedOwner !== null && seatedOwner !== owner) {
      return
    }
    if (seatedOwner !== owner) {
      seatedOwner = owner
      ownerListeners.forEach(fn => fn(owner))
    }
  } else {
    if (seatedOwner !== owner) {
      return
    }
    seatedOwner = null
    ownerListeners.forEach(fn => fn(null))
  }
  const nextSlot = shown ? slot : null
  if (nextSlot !== seatedSlot) {
    seatedSlot = nextSlot
    slotListeners.forEach(fn => fn(nextSlot))
  }
  if (shown === seatedSurface) {
    return
  }
  seatedSurface = shown
  seatedListeners.forEach(fn => fn(shown))
}

/** True while a pick's block holds the reader's own-note surface. */
export function useSeatedSurface(): boolean {
  const [shown, setShown] = useState(seatedSurface)
  useEffect(() => {
    setShown(seatedSurface)
    seatedListeners.add(setShown)
    return () => {
      seatedListeners.delete(setShown)
    }
  }, [])
  return shown
}

/** The seated block's band slot, or null: the band then renders at the capture block. */
export function useSeatedSlot(): HTMLElement | null {
  const [slot, setSlot] = useState<HTMLElement | null>(null)
  useEffect(() => {
    setSlot(seatedSlot)
    slotListeners.add(setSlot)
    return () => {
      slotListeners.delete(setSlot)
    }
  }, [])
  return slot
}

/** The token of the block holding the seat, or null. A block seats only when this is null or
 *  its own token — the first to publish wins, for the page's life. */
export function useSeatedOwner(): string | null {
  const [owner, setOwner] = useState<string | null>(null)
  useEffect(() => {
    setOwner(seatedOwner)
    ownerListeners.add(setOwner)
    return () => {
      ownerListeners.delete(setOwner)
    }
  }, [])
  return owner
}

export interface OwnNoteSignal {
  /** A device cookie exists and `/me` has not answered yet. */
  pending: boolean
  /** This reader has a note (any status) on the product this page shows. */
  hasNote: boolean
  /**
   * The page's one band is open right now. Optional and absent on the PDP, where the ask and
   * the band share a component and neither needs telling; a guide's asks are in three places
   * (strip, rail, the pick the reader owns) and the band in a fourth, so an ask that cannot
   * see it would keep inviting a reader who is already filling the form in.
   * `railAskMode` ignores it — a PDP author is pointed at their note either way.
   */
  bandOpen?: boolean
}

/** Nothing known: what the server render assumes, and what an unmounted section leaves. */
export const NO_OWN_NOTE: OwnNoteSignal = { pending: false, hasNote: false }

let ownNote: OwnNoteSignal = NO_OWN_NOTE
const ownNoteListeners = new Set<(signal: OwnNoteSignal) => void>()

/**
 * What the Owners section publishes. A `/me` refresh while its band is open — the device minted
 * at Publish tells the page (`mintDevice`) — is not an unknown reader: the rail keeps what it
 * showed instead of blanking while the note is in flight. Pure, unit-tested.
 */
export function sectionOwnNoteSignal(input: {
  pending: boolean
  hasNote: boolean
  bandOpen: boolean
}): OwnNoteSignal {
  return { pending: input.pending && !input.bandOpen, hasNote: input.hasNote }
}

/** The Owners section's own-note state, for the rail. */
export function publishOwnNote(signal: OwnNoteSignal): void {
  if (
    signal.pending === ownNote.pending &&
    signal.hasNote === ownNote.hasNote &&
    signal.bandOpen === ownNote.bandOpen
  ) {
    return
  }
  ownNote = signal
  ownNoteListeners.forEach(fn => fn(signal))
}

/**
 * The latest own-note state. The first render always uses the module value, which on a fresh
 * page is `NO_OWN_NOTE` — the same answer the server rendered — so hydration agrees.
 */
export function useOwnNoteSignal(): OwnNoteSignal {
  const [signal, setSignal] = useState(ownNote)
  useEffect(() => {
    setSignal(ownNote)
    ownNoteListeners.add(setSignal)
    return () => {
      ownNoteListeners.delete(setSignal)
    }
  }, [])
  return signal
}
